import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Icon,
  Stack,
  Typography,
} from "@mui/material";
import { ContextSession } from "../services/ContextSession";

const normalizeSegment = (value) => {
  if (!value) return "";
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, "");
};

const getModuleName = (item) => {
  if (!item) return "";
  if (item.modulo) return item.modulo;
  if (Array.isArray(item) && item[0] && Array.isArray(item[0])) {
    return item[0][3] || "";
  }
  return "";
};

const getModuleIcon = (item) => {
  if (!item) return "apps";
  if (item.icono) return item.icono;
  if (Array.isArray(item) && item[0] && Array.isArray(item[0])) {
    return item[0][5] || "apps";
  }
  return "apps";
};

const findFirstLeafWithTrail = (items, trail = []) => {
  if (!Array.isArray(items)) return null;
  for (const item of items) {
    const nextTrail = [...trail, item?.detalle_permiso || item?.permiso].filter(
      Boolean
    );
    if (item?.subpermisos && item.subpermisos.length > 0) {
      const leaf = findFirstLeafWithTrail(item.subpermisos, nextTrail);
      if (leaf) return leaf;
    } else {
      return { leaf: item, trail: nextTrail };
    }
  }
  return null;
};

const getDefaultRoute = (moduleItem) => {
  if (!moduleItem) return "/Inicio";
  if (moduleItem.permisos) {
    const found = findFirstLeafWithTrail(moduleItem.permisos);
    if (found?.trail?.length) {
      const segments = found.trail.map((segment) => normalizeSegment(segment));
      return `/${normalizeSegment(moduleItem.modulo)}/${segments.join("/")}`;
    }
  }
  if (Array.isArray(moduleItem) && Array.isArray(moduleItem[1])) {
    const leaf = moduleItem[1][0];
    const detalle = leaf?.[1] || leaf?.[0];
    const modulo = moduleItem?.[0]?.[3] || "Inicio";
    return `/${normalizeSegment(modulo)}/${normalizeSegment(detalle)}`;
  }
  return "/Inicio";
};

const countLeaves = (items) => {
  if (!Array.isArray(items)) return 0;
  return items.reduce((sum, item) => {
    if (item?.subpermisos && item.subpermisos.length > 0) {
      return sum + countLeaves(item.subpermisos);
    }
    return sum + 1;
  }, 0);
};

const shouldHideModuleCard = (moduleName) =>
  String(moduleName || "").trim().toUpperCase().startsWith("APP");

export default function ModuleHome() {
  const navigate = useNavigate();
  const { sessionData } = React.useContext(ContextSession);

  const modules = useMemo(() => {
    const access = sessionData?.access || [];
    return access
      .map((item) => {
        const name = getModuleName(item);
        if (!name || shouldHideModuleCard(name)) return null;
        return {
          name,
          icon: getModuleIcon(item),
          count: item?.permisos ? countLeaves(item.permisos) : 0,
          defaultRoute: getDefaultRoute(item),
        };
      })
      .filter(Boolean);
  }, [sessionData?.access]);

  const handleSelect = (moduleItem) => {
    localStorage.setItem("activeModule", moduleItem.name);
    window.dispatchEvent(new Event("active-module-changed"));
    navigate(moduleItem.defaultRoute || "/Inicio");
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 120px)",
        p: { xs: 2, md: 4 },
        py: 6,
        mt: { xs: 2, md: 4 },
        borderRadius: 4,
        backgroundImage:
          "linear-gradient(135deg, rgba(107,197,208,0.18), rgba(248,248,248,0.9)), radial-gradient(800px circle at 10% 0%, rgba(17,24,39,0.08), transparent 45%)",
        fontFamily: '"Space Grotesk", "Segoe UI", sans-serif',
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Modulos disponibles
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Selecciona un modulo para ver solo sus opciones de menu.
        </Typography>
      </Stack>

      <Grid container spacing={2} data-tour="module-cards">
        {modules.map((moduleItem) => (
          <Grid item xs={12} sm={6} md={4} key={moduleItem.name}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(15,23,42,0.08)",
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.9), rgba(248,250,252,0.9))",
                backdropFilter: "blur(6px)",
              }}
            >
              <CardActionArea onClick={() => handleSelect(moduleItem)}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        background:
                          "linear-gradient(135deg, rgba(15,118,110,0.12), rgba(14,165,233,0.18))",
                      }}
                    >
                      <Icon sx={{ color: "#0f172a" }}>{moduleItem.icon}</Icon>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {moduleItem.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Gestiona solicitudes y reportes del modulo.
                      </Typography>
                    </Box>
                    {moduleItem.count > 0 && (
                      <Chip
                        size="small"
                        label={`${moduleItem.count} opciones`}
                        sx={{
                          backgroundColor: "rgba(15,118,110,0.12)",
                          color: "#0f172a",
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
        {modules.length === 0 && (
          <Grid item xs={12}>
            <Card elevation={0} sx={{ borderRadius: 3, p: 3 }}>
              <Typography variant="body1">
                No hay modulos asignados para tu usuario.
              </Typography>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
